import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionHeader from "../SectionHeader";
import { BookOpen, MessageSquare, FileText } from "lucide-react";

// Import translations
import finalCTATranslations from "./locales/finalCTA.json";

const FinalCTA: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation("finalCTA");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    // Load translations
    useEffect(() => {
        const ns = "finalCTA";
        ["fa", "en", "ar", "tr"].forEach((lng) => {
            const translations = finalCTATranslations[lng as keyof typeof finalCTATranslations];
            if (translations && !i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, translations, true, true);
            }
        });
    }, [i18n]);

    return (
        <section dir={isRTL ? "rtl" : "ltr"} className="relative py-12 md:py-16 overflow-hidden">
            <div className="container mx-auto px-4 max-w-6xl relative z-10">
                <div className="bg-gradient-to-b from-slate-50 via-white to-blue-50/30 rounded-3xl p-8 md:p-14 border border-slate-200/80 shadow-sm relative overflow-hidden text-center space-y-8">
                    
                    {/* Ambient subtle glow */}
                    <div className="absolute top-0 end-0 w-80 h-80 bg-[#0072CE]/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 start-0 w-80 h-80 bg-[#0038A8]/10 rounded-full blur-3xl pointer-events-none" />

                    {/* Section Header */}
                    <div className="max-w-3xl mx-auto space-y-4 relative z-10">
                        <SectionHeader
                            regularText={t("header.regularText")}
                            highlightedText={t("header.highlightedText")}
                            descriptionHighlight={t("header.descriptionHighlight")}
                            descriptionText={t("header.descriptionText")}
                        />
                    </div>

                    {/* Action Buttons Grid */}
                    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 relative z-10 pt-2">
                        
                        {/* Digital Catalog Button */}
                        <motion.button
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => window.open("https://catalog.delbandcare.com/", "_blank")}
                            className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white text-sm font-bold shadow-xs hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer"
                        >
                            <BookOpen className="w-4 h-4" />
                            <span>{t("buttons.catalog", "مشاهده کاتالوگ دیجیتال")}</span>
                        </motion.button>

                        {/* Contact Button */}
                        <motion.button
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => window.open("https://wa.me/message/GNNOKQBPQVSQB1", "_blank")}
                            className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300 text-sm font-bold shadow-2xs transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer"
                        >
                            <MessageSquare className="w-4 h-4 text-[#0072CE]" />
                            <span>{t("buttons.contact")}</span>
                        </motion.button>

                        {/* Organizational Offer Button (Hidden on Mobile) */}
                        <motion.button
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => window.open("https://survey.porsline.ir/s/gJ1ZAfwb", "_blank")}
                            className="hidden sm:flex w-full sm:w-auto px-7 py-3.5 rounded-xl bg-blue-50 border border-blue-200/80 text-[#0038A8] hover:bg-blue-100/60 text-sm font-bold shadow-2xs transition-all duration-200 items-center justify-center gap-2.5 cursor-pointer"
                        >
                            <FileText className="w-4 h-4 text-[#0038A8]" />
                            <span>{t("buttons.offer")}</span>
                        </motion.button>

                    </div>
                </div>
            </div>
        </section>
    );
};

export default FinalCTA;
